import { getSector, getSectorAzimuthLine } from '@utils/map/sector.utils';
import { cameraRange } from '@utils/sensors/camera/camera.constants';
import { compareCameraRange } from '@utils/sensors/camera/camera.utils';
import { Feature } from 'geojson';

import { FeatureTypeEnum } from '@/types/map.types';
import { CameraActiveMonitoringSensor } from '@/types/sensor/monitoring.types';
import { SubCameraConfiguration, VideoStatus } from '@/types/sensor/sensors';

export const getVideoStatusToDisplay = (
  monitoringSensor: CameraActiveMonitoringSensor,
  selectedSubCameras: SubCameraConfiguration[],
): VideoStatus | null => {
  const configuration = monitoringSensor.configuration;
  const videoStatusesWithFov = monitoringSensor.status.videoStatuses.filter((s) => s.fov !== null && s.fov !== 0);
  const correspondingCamera = selectedSubCameras.find((camera) => camera.sensorId === configuration.id);
  let statusToDisplay = null;
  if (correspondingCamera) {
    statusToDisplay = videoStatusesWithFov.find((status) => status.id === correspondingCamera.cameraId);
  }
  if (!statusToDisplay) {
    statusToDisplay = videoStatusesWithFov.sort((s1, s2) => compareCameraRange(s1, s2)).shift();
  }
  return statusToDisplay ?? null;
};

export const toLadCameraSectorFeatures = (
  monitoringSensors: CameraActiveMonitoringSensor[],
  selectedCameraStreams: SubCameraConfiguration[],
): Feature[] =>
  monitoringSensors
    .filter(
      (monitoringSensor) => !!monitoringSensor.status.videoStatuses.length && monitoringSensor.status.pan !== null,
    )
    .map((monitoringSensor) => {
      const { status, configuration } = monitoringSensor;
      const center = configuration.sensorPosition;

      const displayedVideoStatus = getVideoStatusToDisplay(monitoringSensor, selectedCameraStreams);

      if (!displayedVideoStatus?.fov) {
        return null;
      }

      const bearing1 = status.pan! - displayedVideoStatus.fov / 2;
      const bearing2 = status.pan! + displayedVideoStatus.fov / 2;
      const range = cameraRange[status.type];

      const sector = getSector(center, bearing1, bearing2, range);
      if (!sector) {
        return null;
      }

      return {
        type: 'Feature',
        id: `${FeatureTypeEnum.LAD_CAMERA_SECTOR}-${monitoringSensor.configuration.id}`,
        properties: {
          featureType: FeatureTypeEnum.LAD_CAMERA_SECTOR,
          value: status,
          color: configuration.sensorColor,
        },
        geometry: {
          type: 'Polygon',
          coordinates: sector.geometry.coordinates,
        },
      };
    })
    .filter((sector) => !!sector)
    .map((sector) => sector as Feature);

export const toLadCameraAzimuthLineFeatures = (
  monitoringSensors: CameraActiveMonitoringSensor[],
  selectedCameraStreams: SubCameraConfiguration[],
): Feature[] =>
  monitoringSensors
    .map((monitoringSensor) => {
      const { configuration, status } = monitoringSensor;
      const displayedVideoStatus = getVideoStatusToDisplay(monitoringSensor, selectedCameraStreams);
      if (status.pan === null || !displayedVideoStatus?.fov) {
        return null;
      }

      const range = cameraRange[status.type];

      const azimuthLine = getSectorAzimuthLine(configuration.sensorPosition, range, status.pan);
      if (!azimuthLine) {
        return null;
      }

      return {
        type: 'Feature',
        id: `${FeatureTypeEnum.LAD_CAMERA_AZIMUTH}-${monitoringSensor.configuration.id}`,
        properties: {
          featureType: FeatureTypeEnum.LAD_CAMERA_AZIMUTH,
        },
        geometry: {
          type: 'LineString',
          coordinates: azimuthLine.geometry.coordinates,
        },
      } as Feature;
    })
    .filter((azimuthLine) => !!azimuthLine)
    .map((azimuthLine) => azimuthLine);
